import type { PrintBranding, PrintFormat } from "@/lib/propertyPrint/printOptions"
import { getDefaultMapBounds } from "@/lib/defaultMapBounds"
import { AUTH_MODAL_QUERY_PARAM, WORKOS_LOGOUT_PATH } from "@/constants/auth"
import { resolveBlogBasePath } from "@/components/cms/tenants/registry"

// Server-correct default: resolveBlogBasePath() reads process.env via
// resolveTenantFromEnv(), which Next.js only inlines into the client bundle
// for the *literal* expression `process.env.NEXT_PUBLIC_X` — not dynamic
// property access — so in the browser this silently resolves to the
// default tenant ("/posts") regardless of the actual tenant. Client
// Components must pass the `base` override below, sourced from
// `useTenantSlug()` (see `TenantContext.tsx`), to get the correct path.
const blogBasePath = resolveBlogBasePath()
/**
 * Paths helper for nextjs routing. This will make it easier to change the url structure in the future.
 *
 * We should try and use these paths in the codebase as much as possible,
 * to avoid needing to find all the urls in the codebase and replace them with the new paths.
 */
export const paths = {
  api: {
    canopyTile(z: number, x: number, y: number) {
      return `/api/canopy-tiles/${z}/${x}/${y}`
    },
  },
  home() {
    return "/"
  },
  /** Attach query state to a path resolved by paths helpers or usePathname(). */
  withSearchParams(pathname: string, searchParams: URLSearchParams) {
    const query = searchParams.toString()
    return query ? `${pathname}?${query}` : pathname
  },
  apiMemberClosedListings(uniqueListingIdHash: string) {
    return `/api/member/${encodeURIComponent(uniqueListingIdHash)}/closed-listings`
  },
  apiOfficeClosedListings(uniqueListingIdHash: string) {
    return `/api/office/${encodeURIComponent(uniqueListingIdHash)}/closed-listings`
  },
  /**
   * Base path for homes-for-sale routes.
   * Used for startsWith comparisons in route guards.
   * @returns
   */
  srpBase() {
    return "/homes-for-sale"
  },
  /**
   * Base path for homes-for-rent routes.
   * Used for startsWith comparisons in route guards.
   * @returns
   */
  rentBase() {
    return "/homes-for-rent"
  },
  /**
   * Base path for recently-sold routes.
   * Used for startsWith comparisons in route guards.
   */
  soldBase() {
    return "/recently-sold"
  },
  /**
   * True if the path is a map search route. Used to decide when to use full page
   * load instead of client nav so URL and map state stay in sync (e.g. in Nav/CMSLink).
   */
  isMapSearchPath(path: string): boolean {
    return (
      path.startsWith(this.srpBase()) ||
      path.startsWith(this.rentBase()) ||
      path.startsWith(this.soldBase())
    )
  },
  /**
   * This method is used to get to `/homes-for-sale/homes`.
   * Used as a fallback redirect when no specific location is available,
   * or when the user removes the location pill.
   * @returns
   */
  srpHomes() {
    return `/homes-for-sale/homes`
  },
  srpHomesUS() {
    const { tl, br } = getDefaultMapBounds()
    return `/homes-for-sale/homes?tl=${tl}&br=${br}`
  },
  /**
   * This should be used with _slugs_... This should not be used with a link because
   * it will end up like `/homes-for-sale/homes-for-sale/${slug}`
   * @param slug Must be slugified
   * @returns
   */
  srp(slug: string) {
    return `/homes-for-sale/${slug}`
  },
  /**
   * SRP URL for rentals on the dedicated rent path.
   * @param slug Must be slugified (e.g. "bronx-ny")
   */
  rent(slug: string) {
    return `/homes-for-rent/${slug}`
  },
  /**
   * SRP URL for recently sold listings on the dedicated sold path.
   * @param slug Must be slugified (e.g. "bronx-ny")
   */
  sold(slug: string) {
    return `/recently-sold/${slug}`
  },
  /**
   * Fallback redirect when no specific location is available for sold search.
   */
  soldHomes() {
    return `/recently-sold/homes`
  },
  /**
   * This method is used to get to `/homes-for-rent/homes`.
   * Used as a fallback redirect when no specific location is available for rent,
   * or when the user removes the location pill on a rent SRP.
   */
  rentHomes() {
    return `/homes-for-rent/homes`
  },
  /**
   * @deprecated Prefer paths.rent(slug). Kept for call-site compatibility.
   */
  srpRent(slug: string) {
    return this.rent(slug)
  },

  /**
   * SRP URL for commercial (uses propertyType=Commercial query param).
   * @param slug Must be slugified (e.g. "bronx-ny")
   * @returns
   */
  srpCommercial(slug: string) {
    return `/homes-for-sale/${slug}?propertyType=Commercial`
  },
  /**
   * @deprecated - use baseLdpPath instead
   * @returns
   */
  legacyLdpPath() {
    return "/property"
  },

  /**
   * this should really only be used to check starts with the base LDP path
   *
   * NOTE: if this ever changes, we need to update search-api as well: `src/config/config.service.ts`
   *
   * @returns
   */
  baseLdpPath() {
    return "/home-details"
  },
  // srpCity(category: "city", id: string, state?: string, city?: string) {
  //   return `/${category}/${id}/${state}/${city}`
  // },
  // srpCounty(category: "county", id: string, state?: string, county?: string) {
  //   return `/${category}/${id}/${state}/${county}`
  // },
  // srpZipcode(category: "zipcode", id: string) {
  //   return `/${category}/${id}`
  // },
  /**
   * This method is used to format the URL for the LDP page.
   *
   * NOTE: if this ever changes, we need to update search-api as well: `src/config/config.service.ts`
   * @param address - The address to get the LDP for. Don't forget to slugify the address.
   * @param bupi - The BUPI to get the LDP for.
   * @returns The LDP for the address
   */
  ldp(address: string, bupi: string) {
    return `/home-details/${address}/${bupi}`
  },
  /**
   * Dedicated print page for an LDP. `format` / `branding` select the document
   * (see `src/lib/propertyPrint/printOptions.ts`); defaults are omitted.
   */
  ldpPrint(
    address: string,
    bupi: string,
    options?: { format?: PrintFormat; branding?: PrintBranding }
  ) {
    const params = new URLSearchParams()
    if (options?.format && options.format !== "full") params.set("format", options.format)
    if (options?.branding && options.branding !== "mls") params.set("branding", options.branding)
    const query = params.toString()
    return `/home-details/${address}/${bupi}/print${query ? `?${query}` : ""}`
  },
  /**
   * Resolves a BUPI to the canonical {@link paths.ldp} path (`{ path }`).
   * Used by the SRP map when RVM tiles carry `bupi` but no address slug.
   */
  apiPropertyBupi(
    bupi: string,
    opts?: {
      propertySaleType?: "Sale" | "Rent"
      propertyType?: string
      uniqueListingId?: string
    }
  ) {
    const params = new URLSearchParams({ bupi })
    if (opts?.propertySaleType) {
      params.set("propertySaleType", opts.propertySaleType)
    }
    if (opts?.propertyType) {
      params.set("propertyType", opts.propertyType)
    }
    if (opts?.uniqueListingId) {
      params.set("uniqueListingId", opts.uniqueListingId)
    }
    return `/api/property/bupi?${params.toString()}`
  },
  auth() {
    return "/auth"
  },
  account() {
    return "/account"
  },
  /**
   * Opens the auth lightbox via query on home (AuthLightboxProvider strips the param after open).
   * For in-app navigation prefer `useAuthLightbox().openSignIn` (no route change).
   */
  signIn(returnPathname: string) {
    const q = new URLSearchParams({
      [AUTH_MODAL_QUERY_PARAM]: "sign-in",
      returnPathname,
    })
    return `${this.home()}?${q.toString()}`
  },
  /**
   * Same as sign-in but for registration mode.
   */
  signUp(returnPathname: string) {
    const q = new URLSearchParams({
      [AUTH_MODAL_QUERY_PARAM]: "sign-up",
      returnPathname,
    })
    return `${this.home()}?${q.toString()}`
  },
  /**
   * OAuth authorization (Google, Apple). Redirects to IdP via WorkOS.
   * The SDK does not support `screenHint` for these providers.
   */
  oauthAuthorize(
    provider: "GoogleOAuth" | "AppleOAuth",
    returnPathname: string
  ) {
    const q = new URLSearchParams({
      provider,
      returnPathname,
    })
    return `/api/auth/oauth?${q.toString()}`
  },
  /**
   * @deprecated Use paths.signIn — kept for call-site compatibility; same destination.
   */
  workosLogin(returnPathname: string) {
    return this.signIn(returnPathname)
  },
  /**
   * @deprecated Use paths.signUp — kept for call-site compatibility; same destination.
   */
  workosSignUp(returnPathname: string) {
    return this.signUp(returnPathname)
  },
  /**
   * Full-page WorkOS sign-out (GET clears AuthKit session and redirects).
   * Pass `returnPathname` to redirect back to that path after logout.
   */
  workosLogout(returnPathname?: string) {
    // if (returnPathname) {
    //   return `${WORKOS_LOGOUT_PATH}?returnTo=${encodeURIComponent(returnPathname)}`
    // }
    return WORKOS_LOGOUT_PATH
  },
  accountProfile() {
    return "/account/profile"
  },
  accountSavedSearches() {
    return "/account/saved-searches"
  },
  accountFavorites() {
    return "/account/favorites"
  },
  savedSearches() {
    return "/saved-searches"
  },
  favorites() {
    return "/favorites"
  },
  /**
   * Real Estate Agents routes
   */
  realEstateAgents() {
    return "/real-estate-agents"
  },
  /**
   * @param locationSlug - Must be slugified (e.g., "bronx-ny" or "denver-co")
   * @returns The location-based agents listing page
   */
  realEstateAgentsByLocation(locationSlug: string) {
    return `/real-estate-agents/${locationSlug}`
  },
  /**
   * @param locationSlug - Must be slugified (e.g., "bronx-ny" or "denver-co")
   * @param id - The agent ID (can be full slug like "john-doe-abc123" or just the hash)
   * @returns The individual agent detail page
   */
  realEstateAgent(locationSlug: string, agentSlug: string) {
    return `/real-estate-agents/${locationSlug}/${agentSlug}`
  },
  /**
   * Real Estate Offices routes
   */
  realEstateOffices() {
    return "/real-estate-offices"
  },
  /**
   * @param locationSlug - Must be slugified (e.g., "bronx-ny" or "denver-co")
   * @returns The location-based offices listing page
   */
  realEstateOfficesByLocation(locationSlug: string) {
    return `/real-estate-offices/${locationSlug}`
  },
  /**
   * @param locationSlug - Must be slugified (e.g., "bronx-ny" or "denver-co")
   * @param officeSlug - The office ID (can be full slug or just the hash)
   * @returns The individual office detail page
   */
  realEstateOffice(locationSlug: string, officeSlug: string) {
    return `/real-estate-offices/${locationSlug}/${officeSlug}`
  },
  /**
   * Generate URL for listing by MLS OUID and ListingId
   * @param ouid - The MLS OUID (Originating System Unique Identifier)
   * @param listingId - The listing ID within the MLS
   * @returns The listing URL path (e.g., "/listing/ouid/listingid")
   */
  listingByMls(ouid: string, listingId: string) {
    return `/listing/${ouid}/${listingId}`
  },
  /**
   * Accessibility Statement page
   */
  accessibility() {
    return "/accessibility"
  },
  /**
   * Blog post routes.
   * @param base - Override blog base path (required in Client Components — see note above). Defaults to the server-resolved tenant base path.
   */
  posts(base: string = blogBasePath) {
    return base
  },
  /**
   * @param slug - Blog post slug
   * @param base - Override blog base path (required in Client Components — see note above).
   * @returns The individual blog post detail page
   */
  post(slug: string, base: string = blogBasePath) {
    return `${base}/${slug}`
  },
  /**
   * @param categorySlug - Category slug for filtering posts
   * @param base - Override blog base path (required in Client Components — see note above).
   * @returns The blog listing page filtered by category
   */
  postCategory(categorySlug: string, base: string = blogBasePath) {
    return `${base}/category/${categorySlug}`
  },
  /**
   * News articles (`news` collection). Always /news.
   */
  news() {
    return "/news"
  },
  /**
   * @param slug - News article slug
   */
  newsArticle(slug: string) {
    return `/news/${slug}`
  },
  streetViewImage({
    lat,
    lon,
    address,
    size,
  }: {
    lat?: number
    lon?: number
    address?: string
    size: string
  }) {
    const params = new URLSearchParams({ size })
    const hasCoordinates =
      Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)

    if (address) params.set("address", address)
    if (hasCoordinates) {
      params.set("lat", String(lat))
      params.set("lon", String(lon))
    }
    if (!address && !hasCoordinates) return ""

    return `/api/streetview?${params.toString()}`
  },
  /** Checks Google metadata without downloading a Street View image. */
  streetViewMetadata({
    lat,
    lon,
    address,
  }: {
    lat: number
    lon: number
    address?: string
  }) {
    const params = new URLSearchParams({
      lat: String(lat),
      lon: String(lon),
      metadata: "true",
    })
    if (address) params.set("address", address)
    return `/api/streetview?${params.toString()}`
  },
}
